import { useId } from 'react';
import clsx from 'clsx';

interface SigilProps {
  /**
   * `hero` — top-center на 01-hero (80px, full opacity).
   * `hud` — внутри Kicker HUD (24px, muted).
   * `watermark` — фоновый паттерн атмосферного слоя (120px, opacity 0.04).
   * `card-bg` — декоративный знак внутри карточки (140px, opacity 0.12, без spin).
   * `nav` — глобальный мяч top-right на каждом экране (32px, spin). Igor round-3.
   */
  variant?: 'hero' | 'hud' | 'watermark' | 'card-bg' | 'nav';
  className?: string;
  /**
   * Вращать только мяч (CSS keyframes, 8s linear). По умолчанию `true` для hero/hud,
   * `false` для watermark и card-bg (большие фоновые паттерны не крутятся — лагает +
   * отвлекает). Уважает `prefers-reduced-motion`.
   */
  spinning?: boolean;
}

const SIZE_CLASS: Record<NonNullable<SigilProps['variant']>, string> = {
  hero: 'w-[80px]',
  hud: 'w-[24px]',
  watermark: 'w-[120px]',
  'card-bg': 'w-[140px]',
  nav: 'w-[32px]',
};

/**
 * Sigil `</⚽>` — inline SVG, потому что `<img src=...svg>` не применяет CSS keyframes
 * к внутренним элементам (нельзя вертеть только мяч). Изначальный path-data взят из
 * `/assets/logos/sigil-football-code.svg` без изменений; добавлен `className`
 * на группу мяча и dynamic `id` для filter-defs (useId — чтобы несколько Sigil
 * на странице не конфликтовали).
 */
export function Sigil({ variant = 'hero', className, spinning }: SigilProps) {
  const reactId = useId();
  const filterId = `sigil-filter-${reactId.replace(/[^a-z0-9]/gi, '')}`;
  const ballSpins = spinning ?? (variant !== 'watermark' && variant !== 'card-bg');

  return (
    <svg
      role="presentation"
      aria-hidden="true"
      viewBox="0 0 155 56"
      fill="none"
      className={clsx(
        'block h-auto select-none',
        SIZE_CLASS[variant],
        variant === 'watermark' && 'opacity-[0.04]',
        variant === 'card-bg' && 'opacity-[0.12]',
        variant === 'hud' && 'opacity-80',
        className,
      )}
    >
      {/* Right bracket `>` */}
      <path
        d="M151.334 29.6499L124.495 40.2891V36.7071L147.11 27.9391L124.495 19.0642V15.4821L151.334 26.1748V29.6499Z"
        fill="#F5F5F5"
      />
      {/* Left bracket `<` + slash `/` */}
      <path
        d="M3.02634 29.6499V26.1748L29.865 15.4821V19.0642L7.24995 27.9391L29.865 36.7071V40.2891L3.02634 29.6499ZM57.5553 2.59742L41.6766 42.4811H37.8273L53.8128 2.59742H57.5553Z"
        fill="#F5F5F5"
      />

      {/* Football — spinning group. Center ≈ (87.5, 27.5) within viewBox; `transform-box: fill-box`
          centres rotation on the group's own bounding box (see index.css `.sigil-ball-spin`). */}
      <g
        filter={`url(#${filterId})`}
        className={ballSpins ? 'sigil-ball-spin' : undefined}
      >
        <path
          d="M110.362 24.5733L110.394 24.5973C111.675 25.571 113.584 29.1542 114.193 30.7392C114.383 31.2342 113.84 33.0868 113.709 33.6741C109.401 52.9846 86.1708 60.3733 71.1092 48.0418C59.6928 38.6949 57.9156 21.6736 67.2754 10.4033C70.825 6.06312 75.69 2.99534 81.1363 1.66286C81.8495 1.48918 82.7354 1.25618 83.4534 1.1709C84.0374 1.25865 83.9831 0.968793 84.6244 1.52403C84.0827 1.64131 83.2085 1.79527 82.7814 2.1142C80.5551 3.77624 78.1905 7.01542 76.3506 9.15255C73.767 9.47093 69.9983 9.7485 68.3075 12.0393C66.4512 14.5548 64.1138 19.4944 64.1528 22.6548C64.171 24.1295 67.1213 26.8989 68.1049 27.9719C67.6874 30.481 68.6629 36.0731 69.1328 38.6547C68.254 38.5069 67.1667 38.3962 66.3359 38.2363C66.919 39.117 67.459 40.025 68.0476 40.8998C71.0059 45.2967 75.7028 48.9114 80.7666 50.5398C79.6301 48.8395 78.1232 45.5775 77.0299 44.2379C78.377 43.6917 79.7151 43.1238 81.0438 42.5341C81.9528 42.1337 83.0863 41.5917 84.0113 41.2935C85.0681 41.5177 86.6366 42.287 87.6739 42.707C90.1339 43.7029 92.7239 44.977 95.2253 45.8C94.6486 47.2737 93.8021 49.8717 93.375 51.3989C100.02 50.0501 105.409 46.9018 109.491 41.3902C107.493 41.5918 105.202 41.5307 103.19 41.4731C103.747 39.1196 103.572 36.4542 103.248 34.0656C103.116 33.0939 102.888 32.1038 102.806 31.1278C105.242 29.6811 108.441 26.7236 110.362 24.5733Z"
          fill="#F5F5F5"
        />
        <path
          d="M92.7818 26.4015C92.7839 26.413 92.786 26.4244 92.7881 26.4358C92.7888 26.4551 92.7736 26.6802 92.7738 26.6799C90.6507 28.2423 84.3369 31.586 82.7425 33.0887C80.6064 31.0515 76.1763 27.4327 73.7017 25.6099C73.9917 24.8047 74.5626 23.6802 74.9403 22.881C75.7909 20.9003 76.7542 18.9545 77.639 16.9875C77.9403 16.3177 78.212 15.7753 78.3825 15.0566C82.1735 15.008 86.6106 14.9763 90.323 15.8209C90.46 16.7925 90.9105 18.4154 91.1494 19.4493C91.6758 21.7283 92.1342 24.1731 92.7818 26.4015Z"
          fill="#F5F5F5"
        />
        <path
          d="M107.849 10.7264C107.159 11.6854 106.265 12.9564 105.662 13.9561C102.988 11.0724 99.7387 9.78227 96.1877 8.44081C95.6029 7.16711 95.1517 5.79383 94.597 4.48776C94.3399 3.83175 93.8075 2.92732 93.4844 2.15137C98.1542 2.68799 101.751 4.75516 105.148 7.91501C106.061 8.76423 106.967 9.81501 107.849 10.7264Z"
          fill="#F5F5F5"
        />
        <path
          d="M83.4534 1.17079C83.8565 0.963921 87.2219 1.03838 87.9006 1.06094L87.9004 1.15732L88.0031 1.06956L88.0825 1.10615L88.0021 1.13026L88.031 1.06745L87.968 1.05819L87.9183 1.14411C86.7492 1.31302 85.9569 1.2723 84.6244 1.52393C83.983 0.968689 84.0373 1.25855 83.4534 1.17079Z"
          fill="#F5F5F5"
        />
      </g>

      <defs>
        {/* Generous bbox so the rotating ball never clips against the filter region. */}
        <filter
          id={filterId}
          x="55"
          y="-5"
          width="65"
          height="65"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="0.282934" result="blur" />
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.999 0.999"
            numOctaves={3}
            seed={8453}
          />
          <feDisplacementMap
            in="blur"
            scale="2.057"
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </defs>
    </svg>
  );
}
